<script lang="ts" setup>
import { nanoid } from 'nanoid'
import type { ScenarioChatForm_Message_SelectionsType } from '../types'
import DolphinTextarea from '../../../atoms/DolphinTextarea.vue'
import BasicSelections from './BasicSelections/BasicSelections.vue'
import BookingRequestSelections from './BookingRequestSelections/BookingRequestSelections.vue'
import {
  ScenarioChatFormSchema_Message_SelectionsType,
  ScenarioChatFormSchema_Message_SelectionsType_Basic,
  ScenarioChatFormSchema_Message_SelectionsType_BookingRequest,
  ScenarioChatFormSchema_Message_SelectionsType_None,
} from '../types'
import { computed, reactive } from 'vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import type { Option, OptionValueType } from '@/types/app'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import * as v from 'valibot'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import { useInternalScenarioChatFormStore } from '../form/form'

const props = defineProps<{
  messageClientOnlyId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const formStore = reactive(useInternalScenarioChatFormStore())

const currentMessage = computed(
  () => formStore.formValues.messages[props.messageClientOnlyId]
)

const currentSelections = computed(() => currentMessage.value.selections)

const currentSelectionsType = computed(() => {
  return currentSelections.value.type
})

const selectionsTypeOptions: Array<
  Option<ScenarioChatForm_Message_SelectionsType>
> = [
  {
    value: ScenarioChatFormSchema_Message_SelectionsType_Basic.literal,
    text: '選択肢',
  },
  {
    value: ScenarioChatFormSchema_Message_SelectionsType_BookingRequest.literal,
    text: '日程調整',
  },
  {
    value: ScenarioChatFormSchema_Message_SelectionsType_None.literal,
    text: 'なし',
  },
]

const handleUpdateBody = (value: string) => {
  formStore.updateMessage(props.messageClientOnlyId, draft => {
    draft.body = value
  })
}

const updateSelections = (value: OptionValueType) => {
  if (currentSelectionsType.value == undefined) {
    throw new Error('currentSelectionsType is undefined')
  }

  const newSelectionsType = v.parse(
    ScenarioChatFormSchema_Message_SelectionsType,
    value
  )

  if (currentSelectionsType.value === newSelectionsType) {
    return
  }

  // 選択肢の種類を変更すると内容が破棄されるので、その前に内容があるかどうかを確認する
  const hasSelectionsInput = (() => {
    if (currentSelections.value == undefined) {
      return false
    }

    switch (currentSelections.value.type) {
      case ScenarioChatFormSchema_Message_SelectionsType_Basic.literal:
        // ラベルのみを確認している
        // アクションや次のメッセージの入力も確認してもいいが、実装上でサボっているだけ
        return currentSelections.value.basicSelections.some(
          selection => selection.label !== ''
        )
      case ScenarioChatFormSchema_Message_SelectionsType_BookingRequest.literal:
        return currentSelections.value.fallbackUrl !== ''
      case ScenarioChatFormSchema_Message_SelectionsType_None.literal:
        return false
      default:
        assertExhaustive(currentSelections.value)
    }
  })()

  if (hasSelectionsInput) {
    const confirmed = window.confirm(
      '選択肢の種類を変更すると内容が破棄されます。内容を破棄してよろしいですか？'
    )
    if (!confirmed) {
      return
    }
  }

  const newSelections = (() => {
    switch (newSelectionsType) {
      case ScenarioChatFormSchema_Message_SelectionsType_Basic.literal:
        return {
          type: ScenarioChatFormSchema_Message_SelectionsType_Basic.literal,
          basicSelections: [
            {
              guid: undefined,
              clientOnlyId: nanoid(),
              label: '',
              actions: [],
              nextScenarioChatMessageClientOnlyId: undefined,
            },
            {
              guid: undefined,
              clientOnlyId: nanoid(),
              label: '',
              actions: [],
              nextScenarioChatMessageClientOnlyId: undefined,
            },
          ],
        }
      case ScenarioChatFormSchema_Message_SelectionsType_BookingRequest.literal:
        return {
          type: ScenarioChatFormSchema_Message_SelectionsType_BookingRequest.literal,
          fallbackUrl: '',
        }
      case ScenarioChatFormSchema_Message_SelectionsType_None.literal:
        return {
          type: ScenarioChatFormSchema_Message_SelectionsType_None.literal,
        }
    }
  })()

  formStore.updateMessage(props.messageClientOnlyId, draft => {
    draft.selections = newSelections
  })
}

const handleClickDeleteMessageButton = () => {
  const confirmed = window.confirm('メッセージを削除しますか？')
  if (!confirmed) {
    return
  }

  formStore.deleteMessage(props.messageClientOnlyId)
}
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.headControls">
      <button @click.stop.prevent="emit('close')" :class="$style.closeButton">
        <IconChevron :class="$style.closeButtonIcon" />
        <span>閉じる</span>
      </button>
    </div>

    <div :class="$style.messageWrapper">
      <div>
        <label :class="$style.headerLabel">送信するメッセージ 本文</label>

        <DolphinTextarea
          :model-value="currentMessage.body"
          @update:model-value="handleUpdateBody"
          :class="$style.body"
          :rows="8"
          placeholder="メッセージ本文を入力する..."
        />
      </div>

      <div :class="$style.selections">
        <label :class="$style.headerLabel">選択肢</label>
        <div :class="$style.selectionOptions">
          <label
            :class="$style.selectionOption"
            :key="index"
            v-for="(option, index) in selectionsTypeOptions"
          >
            <DolphinRadioButton
              name="selectionsType"
              :model-value="currentSelectionsType"
              @update:model-value="updateSelections"
              :value="option.value"
            />
            <span :class="$style.option__text">
              {{ option.text }}
            </span>
          </label>
        </div>

        <div :class="$style.selectionInner">
          <BasicSelections
            v-if="currentSelections.type === 'BasicSelections'"
            :selectedMessageClientOnlyId="messageClientOnlyId"
            :basicSelections="currentSelections"
            :isDeletable="true"
          />
          <BookingRequestSelections
            v-if="currentSelections.type === 'BookingRequestSelections'"
            :selectedMessageClientOnlyId="messageClientOnlyId"
          />
        </div>
      </div>
    </div>
    <div :class="$style.deleteMessageWrapper">
      <button
        @click.stop.prevent="handleClickDeleteMessageButton"
        :class="$style.deleteMessageButton"
      >
        <span>メッセージを削除する</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" module>
.container {
  background-color: #ffffff;
  border-left: 1px solid #cccccc;
  padding: 16px 16px 32px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.headControls {
  display: flex;
  justify-content: flex-end;
}

.closeButton {
  display: flex;
  align-items: center;
  color: $color-text-and-object-medium-emphasis;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
}

.closeButtonIcon {
  width: 24px;
  height: 24px;
}

.messageWrapper {
  margin-top: 16px;
}

.messageErrors {
  margin-bottom: 16px;
}

.headerLabel {
  font-size: 18px;
  font-weight: 500;
}

.body {
  width: 100%;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.4;
  resize: none;
  margin-top: 8px;
}

.selectionOptions {
  margin-top: 16px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.selectionOption {
  display: flex;
  gap: 4px;
  align-items: center;
}

.selections {
  margin-top: 16px;
}

.selectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selectionInner {
  margin-top: 16px;
}

.removeSelectionButton {
  display: inline-flex;
  border-radius: 999em;
  padding: 8px;

  &:hover {
    color: $color-caution-accent;
    background-color: $color-background-base;
  }
}

.removeSelectionButtonIcon {
  width: 16px;
  height: 16px;
}

.inputWrapper {
  margin-top: 12px;
}

.input {
  font-size: 12px;
  margin-top: 4px;
  width: 100%;
}

.nextMessage {
  font-size: 12px;
  font-weight: bold;
}

.nextChatMessageSelector {
  width: 100%;
  margin-top: 4px;
}

.deleteMessageWrapper {
  margin-top: 88px;
  display: flex;
  justify-content: flex-end;
}

.deleteMessageButton {
  display: inline-flex;
  align-items: center;
  color: $color-text-and-object-low-emphasis;
  font-size: 14px;

  &:hover {
    color: $color-caution-accent;
    text-decoration: underline;
  }
}
</style>
